import React, { useCallback, useEffect, useState } from 'react';
import { Star, Trash2, Clock, Save } from 'lucide-react';
import {
  listSavedTemplates,
  createSavedTemplate,
  updateSavedTemplate,
  deleteSavedTemplate,
  SAVED_TEMPLATES_CHANGED_EVENT,
} from '../lib/reportTemplatesApi';
import { getRecentReports, removeRecentReport, RECENT_REPORTS_CHANGED_EVENT } from '../lib/recentReports';
import { runSavedReportGeneration } from '../lib/generateReport';
import { reportTitleForType } from '../lib/reportTitle';
import { configScopeLabel, formatStamp } from '../lib/configLabel';
import parseUtcTimestamp from '../../../utils/parseUtcTimestamp';
import { useReportWindows } from '../windows/ReportWindowsContext';

/**
 * "Saved" + "Recent" rail on the Reports page. Saved configurations are
 * fetched from the backend (backend/app/api/api_report_templates.py) and
 * refresh whenever any component mutates them (the "Save configuration"
 * button lives on ReportWindow, elsewhere in the tree - see
 * SAVED_TEMPLATES_CHANGED_EVENT). Recents are localStorage-only, captured by
 * ReportsPage.handleGenerateReport at the same seam as Save. Clicking either
 * kind re-runs the generation through the shared helper
 * (lib/generateReport.runSavedReportGeneration) - the same code path a popup
 * submit uses.
 */
const SavedConfigsRail = ({ darkMode }) => {
  const { openReportWindow } = useReportWindows();
  const [saved, setSaved] = useState([]);
  const [savedLoading, setSavedLoading] = useState(true);
  const [savedError, setSavedError] = useState(null);
  const [recents, setRecents] = useState([]);
  const [runError, setRunError] = useState(null);
  const [runningKey, setRunningKey] = useState(null);

  const refreshSaved = useCallback(() => {
    setSavedLoading(true);
    listSavedTemplates()
      .then((rows) => {
        setSaved(rows || []);
        setSavedError(null);
      })
      .catch((e) => setSavedError(e.message || 'Failed to load saved configurations'))
      .finally(() => setSavedLoading(false));
  }, []);

  useEffect(() => {
    refreshSaved();
    window.addEventListener(SAVED_TEMPLATES_CHANGED_EVENT, refreshSaved);
    return () => window.removeEventListener(SAVED_TEMPLATES_CHANGED_EVENT, refreshSaved);
  }, [refreshSaved]);

  useEffect(() => {
    const refreshRecents = () => setRecents(getRecentReports());
    refreshRecents();
    window.addEventListener(RECENT_REPORTS_CHANGED_EVENT, refreshRecents);
    return () => window.removeEventListener(RECENT_REPORTS_CHANGED_EVENT, refreshRecents);
  }, []);

  const runConfig = useCallback(
    async (key, { reportType, params, name, touchId }) => {
      setRunError(null);
      setRunningKey(key);
      try {
        await runSavedReportGeneration({ reportType, requestParams: params, reportName: name, openReportWindow })
          .catch((err) => { if (err && err.message === 'POPUP_BLOCKED') window.alert('Allow pop-ups for this site to open the report.'); });
        if (touchId != null) {
          updateSavedTemplate(touchId, { touch_last_used: true }).catch(() => {});
        }
      } catch (e) {
        setRunError(e.message || 'Failed to generate report');
      } finally {
        setRunningKey(null);
      }
    },
    [openReportWindow]
  );

  const handleRunSaved = (config) =>
    runConfig(`saved-${config.id}`, {
      reportType: config.report_type,
      params: config.params,
      name: reportTitleForType(config.report_type),
      touchId: config.id,
    });

  const handleRunRecent = (recent, idx) =>
    runConfig(`recent-${idx}`, {
      reportType: recent.reportType,
      params: recent.requestParams,
      name: recent.reportName,
    });

  const handleToggleFavorite = (config, e) => {
    e.stopPropagation();
    updateSavedTemplate(config.id, { is_favorite: !config.is_favorite }).catch(
      (err) => setRunError(err.message || 'Failed to update favorite')
    );
  };

  const handleDelete = (config, e) => {
    e.stopPropagation();
    deleteSavedTemplate(config.id).catch((err) => setRunError(err.message || 'Failed to delete'));
  };

  // Promote a recent to a saved configuration - the same write the report
  // window's "Save configuration" button makes, so the SAVED list above
  // refreshes through SAVED_TEMPLATES_CHANGED_EVENT with no extra wiring.
  const handleSaveRecent = (recent, e) => {
    e.stopPropagation();
    createSavedTemplate({
      name: recent.reportName,
      reportType: recent.reportType,
      params: recent.requestParams,
    }).catch((err) => setRunError(err.message || 'Failed to save configuration'));
  };

  const handleDeleteRecent = (recent, e) => {
    e.stopPropagation();
    removeRecentReport(recent);
  };

  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const border = darkMode ? 'border-white/10' : 'border-gray-200';
  const divide = darkMode ? 'divide-white/10' : 'divide-gray-200';
  const rowHover = darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50';

  const rowProps = (onRun) => ({
    role: 'button',
    tabIndex: 0,
    onClick: onRun,
    onKeyDown: (e) => {
      if (e.key === 'Enter') onRun();
    },
  });

  return (
    <div className="mt-8">
      {runError && <div className="mb-2 text-xs text-red-500">{runError}</div>}

      <h2 className={`mb-2 text-xs font-semibold uppercase tracking-wide ${textMuted}`}>Saved</h2>
      {savedLoading ? (
        <div className={`text-xs ${textMuted}`}>Loading saved configurations...</div>
      ) : savedError ? (
        <div className="text-xs text-red-500">{savedError}</div>
      ) : saved.length === 0 ? (
        <div className={`text-xs ${textMuted}`}>No saved configurations yet - save one from a report window.</div>
      ) : (
        <ul className={`divide-y overflow-hidden rounded border ${border} ${divide}`}>
          {saved.map((config) => (
            <li
              key={config.id}
              {...rowProps(() => handleRunSaved(config))}
              className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm ${rowHover} ${
                runningKey === `saved-${config.id}` ? 'opacity-60' : ''
              }`}
            >
              <div className="min-w-0">
                <div className={`truncate ${textPrimary}`}>{config.name}</div>
                <div className={`truncate text-xs ${textMuted}`}>
                  {[
                    reportTitleForType(config.report_type),
                    configScopeLabel(config.params),
                    `saved ${formatStamp(parseUtcTimestamp(config.created_at))}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  title={config.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                  aria-pressed={config.is_favorite}
                  onClick={(e) => handleToggleFavorite(config, e)}
                  className={config.is_favorite ? 'text-yellow-400' : textMuted}
                >
                  <Star className="h-4 w-4" fill={config.is_favorite ? 'currentColor' : 'none'} />
                </button>
                <button type="button" title="Delete" onClick={(e) => handleDelete(config, e)} className={textMuted}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <h2 className={`mb-2 mt-6 text-xs font-semibold uppercase tracking-wide ${textMuted}`}>Recent</h2>
      {recents.length === 0 ? (
        <div className={`text-xs ${textMuted}`}>No recent reports yet.</div>
      ) : (
        <ul className={`divide-y overflow-hidden rounded border ${border} ${divide}`}>
          {recents.map((recent, idx) => (
            <li
              key={`${recent.reportType}-${idx}`}
              {...rowProps(() => handleRunRecent(recent, idx))}
              className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm ${rowHover} ${
                runningKey === `recent-${idx}` ? 'opacity-60' : ''
              }`}
            >
              <div className="flex min-w-0 items-center gap-2">
                <Clock className={`h-3.5 w-3.5 shrink-0 ${textMuted}`} />
                <div className="min-w-0">
                  <div className={`truncate ${textPrimary}`}>{recent.reportName}</div>
                  <div className={`truncate text-xs ${textMuted}`}>
                    {[configScopeLabel(recent.requestParams), formatStamp(Date.parse(recent.generatedAt))]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  title="Save configuration"
                  onClick={(e) => handleSaveRecent(recent, e)}
                  className={textMuted}
                >
                  <Save className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  title="Remove from recents"
                  onClick={(e) => handleDeleteRecent(recent, e)}
                  className={textMuted}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SavedConfigsRail;
